import React, { useEffect, useState } from "react";
import { fmtDateTime } from "@/lib/dateUtils";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, CalendarClock, Sparkles, HardDrive, ScrollText, Bell,
  ImagePlus, BrainCircuit, Plus, ArrowUpRight, Wand2, Zap,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BookShelfStrip } from "@/pages/BookShelf";
import { InstantClassModal } from "@/components/live/InstantClassModal";
import { openMeetTracked, autoEndClass } from "@/lib/meetWindow";

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingInstant, setStartingInstant] = useState(false);
  const [instant, setInstant] = useState(null); // { class, meet_url } → modal

  // Instant Google Meet — fresh Meet link right now, no scheduling.
  // Closing the Meet tab auto-ends the class.
  const startInstantClass = async () => {
    if (startingInstant) return;
    setStartingInstant(true);
    try {
      const { data } = await api.post("/classes/instant", { duration_minutes: 60 });
      setInstant(data);
      openMeetTracked(data.meet_url, () => autoEndClass(data.class.id, { onDone: () => setInstant(null) }), data.class.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start an instant class");
    } finally {
      setStartingInstant(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/teacher/dashboard");
      setData(data);
    } catch { /* not a teacher / network — empty-state below */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-neutral-500">Loading dashboard…</div>;
  if (!data) return <div className="text-neutral-500" data-testid="teacher-dashboard-error">Couldn't load the teacher dashboard.</div>;
  const s = data?.stats || {};
  const storagePct = s.storage_quota_mb ? Math.min(100, (s.storage_used_mb / s.storage_quota_mb) * 100) : 0;
  const isEmpty = (s.total_students || 0) === 0;

  return (
    <div className="space-y-8" data-testid="teacher-dashboard">
      {/* Greeting */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ overview</div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter">Hello, {user?.full_name?.split(" ")[0]}.</h1>
          <p className="text-neutral-600 mt-1 text-sm">Here's what's happening in your tuition today.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={startInstantClass}
            disabled={startingInstant}
            data-testid="dashboard-start-instant-class"
            className="text-sm font-bold px-6 py-3 rounded-lg bg-[#00897B] hover:bg-[#00A48F] text-white inline-flex items-center gap-2 transition-all hover:shadow-[0_6px_24px_rgba(0,137,123,0.4)] hover:scale-[1.02] disabled:opacity-60"
            title="Start a fresh Google Meet right now and invite your students"
          >
            <Zap size={16} /> {startingInstant ? "Creating Meet…" : "Start your class"}
          </button>
          <QuickAction to="/teacher/students" icon={Plus} label="Add student" />
        </div>
      </div>

      {/* Empty-state seed banner — only shows when account is truly fresh */}
      {isEmpty && (
        <div className="relative overflow-hidden rounded-2xl border border-[#FFB400]/40 bg-gradient-to-br from-[#FFB400]/15 via-[#FFD600]/10 to-transparent p-6 lg:p-8" data-testid="seed-banner">
          <div aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #FFB400, transparent 60%)" }} />
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white mb-1 inline-flex items-center gap-2">
                <Wand2 size={12} /> Brand new account
              </div>
              <div className="font-display font-bold text-xl tracking-tight">Let's give your dashboard a test drive.</div>
              <p className="text-sm text-neutral-700 mt-1">Start your class or add your first student using the buttons above.</p>
            </div>
          </div>
        </div>
      )}

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
        <Stat label="Total students" value={s.total_students || 0} cap={`/ ${s.student_limit}`} icon={Users} />
        <Stat label="Active batches" value={s.active_batches || 0} icon={CalendarClock} />
        <Stat label="AI credits" value={s.ai_credits || 0} icon={Sparkles} accent />
        <Stat label="Storage" value={`${(s.storage_used_mb || 0).toFixed(0)} / ${s.storage_quota_mb} MB`} icon={HardDrive} progress={storagePct} />
        <Stat label="Pending fees" value={s.pending_fees || 0} icon={Bell} />
        <Stat label="Notifications" value={s.notifications || 0} icon={Bell} />
        <Stat label="AI quizzes" value={(data.recent_quizzes || []).length} icon={ScrollText} />
        <Stat label="Generated content" value={s.generated_content || 0} icon={ImagePlus} />
      </div>

      {/* KPI: explicit willingness-to-pay question (one-time, dismissable by answering) */}
      <WillingToPayCard />

      {/* Two columns: today + upcoming */}
      <div className="grid lg:grid-cols-3 gap-6">
        <ListCard title="Today's schedule" empty="No classes today. Plan one →" link="/teacher/classes"
          items={data.today_classes} render={(c) => (
            <div key={c.id} className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">
              <div>
                <div className="font-medium text-sm">{c.title}</div>
                <div className="text-xs text-neutral-500">{c.subject} · {c.start_time?.slice(11, 16)}</div>
              </div>
              <Link to={`/live/${c.id}`} data-testid={`join-class-${c.id}`} className="text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5">Join</Link>
            </div>
          )}
        />

        <ListCard title="Upcoming classes" empty="Nothing upcoming." link="/teacher/classes" colSpan={2}
          items={data.upcoming_classes?.slice(0, 6)} render={(c) => (
            <div key={c.id} className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">
              <div>
                <div className="font-medium text-sm">{c.title}</div>
                <div className="text-xs text-neutral-500">{c.subject} · {fmtDateTime(c.start_time)} · {c.meeting_platform === "google_meet" ? "Google Meet" : c.meeting_platform}</div>
              </div>
              <Link to={`/live/${c.id}`} className="text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5">Open</Link>
            </div>
          )}
        />
      </div>

      {/* Recent quizzes */}
      <ListCard title="Recent AI quizzes" empty="No quizzes yet. Generate one →" link="/teacher/quizzes"
        items={data.recent_quizzes} render={(q) => (
          <div key={q.id} className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">
            <div>
              <div className="font-medium text-sm">{q.title}</div>
              <div className="text-xs text-neutral-500">{q.subject} · {(q.questions || []).length} questions · {q.topic}</div>
            </div>
            <Link to="/teacher/quizzes" className="text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5">Open</Link>
          </div>
        )}
      />

      <BookShelfStrip />

      {instant && <InstantClassModal data={instant} onClose={() => setInstant(null)} onEnded={() => setInstant(null)} />}
    </div>
  );
}


function QuickAction({ to, icon: Icon, label, featured }) {
  return (
    <Link
      to={to}
      data-testid={`quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border-2 ${featured ? "bg-[#FFD600] text-[#0A0C10] border-transparent btn-brutal-shadow" : "bg-white border-neutral-200 hover:border-[#0A0C10]"}`}
    >
      <Icon size={16} /> {label}
    </Link>
  );
}

function Stat({ label, value, cap = "", icon: Icon, accent, progress }) {
  return (
    <div className="bg-white p-5 flex flex-col justify-between min-h-[120px]">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">{label}</div>
        <Icon size={16} strokeWidth={1.5} className={accent ? "text-[#FFD600]" : "text-neutral-400"} />
      </div>
      <div className="flex items-baseline gap-1">
        <div className={`font-display font-bold text-3xl tracking-tight ${accent ? "text-[#0A0C10]" : ""}`}>{value}</div>
        {cap && <div className="text-sm text-neutral-500">{cap}</div>}
      </div>
      {progress != null && (
        <div className="mt-2 h-1.5 bg-neutral-100">
          <div className="h-full bg-[#002FA7]" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}

function ListCard({ title, items, render, empty, link, colSpan }) {
  return (
    <div className={`bg-white border border-neutral-200 p-6 ${colSpan === 2 ? "lg:col-span-2" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold">{title}</div>
        {link && <Link to={link} className="text-xs text-neutral-500 hover:text-[#0A0C10] inline-flex items-center gap-1">All <ArrowUpRight size={12} /></Link>}
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200">{empty}</div>
      ) : items.map(render)}
    </div>
  );
}

// KPI — asks once whether the teacher would pay after the trial (genuine commercial-intent data).
function WillingToPayCard() {
  const [answered, setAnswered] = useState(() => localStorage.getItem("wtp.answered") === "1");
  const send = async (willing) => {
    try {
      await api.post("/kpi/willing-to-pay", { willing });
      localStorage.setItem("wtp.answered", "1");
      setAnswered(true);
      toast.success("Thanks — your feedback helps us improve!");
    } catch { toast.error("Could not save your answer"); }
  };
  if (answered) return null;
  return (
    <div className="bg-gradient-to-r from-[#FFF8E1] to-white border border-[#FFB400]/40 rounded-md p-4 flex flex-wrap items-center gap-3" data-testid="wtp-card">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ quick question</div>
        <div className="text-sm font-semibold text-neutral-800">Would you pay for TeachingSoftware.ai after your trial ends?</div>
      </div>
      <button onClick={() => send(true)} data-testid="wtp-yes" className="px-5 py-2 rounded-md bg-[#0A0C10] text-white text-xs font-bold hover:bg-black">Yes, I would</button>
      <button onClick={() => send(false)} data-testid="wtp-no" className="px-5 py-2 rounded-md border border-neutral-300 text-xs font-semibold hover:border-black">Not yet</button>
    </div>
  );
}
